import * as i0 from '@angular/core';
import { NgModule, Injectable } from '@angular/core';
import { CommonModule } from '@angular/common';
import { lastValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import * as i1 from '@angular/common/http';
let AngularHighfieldwebapiModule = /*#__PURE__*/(() => {
  class AngularHighfieldwebapiModule {
    static forRoot() {
      return {
        ngModule: AngularHighfieldwebapiModule
      };
    }
    static {
      this.ɵfac = function AngularHighfieldwebapiModule_Factory(t) {
        return new (t || AngularHighfieldwebapiModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: AngularHighfieldwebapiModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        imports: [CommonModule]
      });
    }
  }
  return AngularHighfieldwebapiModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class APIResponse {
  constructor(responseCode, responseMessage, responseData, responseError = "") {
    this.responseCode = responseCode;
    this.responseMessage = responseMessage;
    this.responseData = responseData;
    this.responseError = responseError;
  }
}
class GenericAPIResponse {}
let APIService = /*#__PURE__*/(() => {
  class APIService {
    constructor(httpClient) {
      this.httpClient = httpClient;
    }
    //#region Promise functions
    async getAsync(serviceBaseURL, requestUri, headers) {
      const response = await new Promise((resolve, reject) => {
        lastValueFrom(this.httpClient.get(`${serviceBaseURL}${requestUri}`, {
          headers,
          observe: 'response'
        })).then(res => {
          resolve(new APIResponse(res.status, res.statusText, res.body));
        }, err => {
          reject(new APIResponse(err.status, err.statusText, err.message, err.error));
        });
      });
      return response;
    }
    async postAsync(serviceBaseURL, requestUri, headers, body) {
      const response = await new Promise((resolve, reject) => {
        lastValueFrom(this.httpClient.post(`${serviceBaseURL}${requestUri}`, body, {
          headers,
          observe: 'response'
        })).then(res => {
          resolve(new APIResponse(res.status, res.statusText, res.body));
        }, err => {
          reject(new APIResponse(err.status, err.statusText, err.message, err.error));
        });
      });
      return response;
    }
    async putAsync(serviceBaseURL, requestUri, headers, body) {
      const response = await new Promise((resolve, reject) => {
        lastValueFrom(this.httpClient.put(`${serviceBaseURL}${requestUri}`, body, {
          headers,
          observe: 'response'
        })).then(res => {
          resolve(new APIResponse(res.status, res.statusText, res.body));
        }, err => {
          reject(new APIResponse(err.status, err.statusText, err.message, err.error));
        });
      });
      return response;
    }
    async getFileAsync(serviceBaseURL, requestUri, headers) {
      const response = await new Promise((resolve, reject) => {
        lastValueFrom(this.httpClient.get(`${serviceBaseURL}${requestUri}`, {
          headers,
          observe: 'response',
          responseType: "blob"
        })).then(res => {
          resolve(new APIResponse(res.status, res.statusText, res));
        }, err => {
          reject(new APIResponse(err.status, err.statusText, err.message, err.error));
        });
      });
      return response;
    }
    async deleteAsync(serviceBaseURL, requestUri, headers) {
      const response = await new Promise((resolve, reject) => {
        lastValueFrom(this.httpClient.delete(`${serviceBaseURL}${requestUri}`, {
          headers,
          observe: 'response'
        })).then(res => {
          resolve(new APIResponse(res.status, res.statusText, res.body));
        }, err => {
          reject(new APIResponse(err.status, err.statusText, err.message, err.error));
        });
      });
      return response;
    }
    //#endregion
    //#region Observable functions
    getObservable(serviceBaseURL, requestUri, headers) {
      return this.httpClient.get(`${serviceBaseURL}${requestUri}`, {
        headers,
        observe: 'response'
      }).pipe(map(httpResponse => this.processResponse(httpResponse)));
    }
    postObservable(serviceBaseURL, requestUri, headers, body) {
      return this.httpClient.post(`${serviceBaseURL}${requestUri}`, body, {
        headers,
        observe: 'response'
      }).pipe(map(httpResponse => this.processResponse(httpResponse)));
    }
    putObservable(serviceBaseURL, requestUri, headers, body) {
      return this.httpClient.put(`${serviceBaseURL}${requestUri}`, body, {
        headers,
        observe: 'response'
      }).pipe(map(httpResponse => this.processResponse(httpResponse)));
    }
    deleteObservable(serviceBaseURL, requestUri, headers) {
      return this.httpClient.delete(`${serviceBaseURL}${requestUri}`, {
        headers,
        observe: 'response'
      }).pipe(map(httpResponse => this.processResponse(httpResponse)));
    }
    processResponse(response) {
      // API call returns a GenericAPIResponse<T>, just pass it through. 
      if (response.body?.responseData !== undefined) {
        return response.body;
      }
      // API call does NOT return a GenericAPIResponse<T>, let's make do the best we can to mimic it.
      if (response.body) {
        // Construct a GenericAPIResponse<T> object as best we can
        // Trust the return type implicitly 
        return {
          responseCode: 200,
          responseMessage: '',
          responseData: response.body,
          responseError: ''
        };
      } else {
        return null;
      }
    }
    static {
      this.ɵfac = function APIService_Factory(t) {
        return new (t || APIService)(i0.ɵɵinject(i1.HttpClient));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: APIService,
        factory: APIService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return APIService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let StoredSettingService = /*#__PURE__*/(() => {
  class StoredSettingService {
    constructor() {
      this.getSetting = key => {
        const item = localStorage.getItem(key);
        if (item && item !== 'undefined') {
          return JSON.parse(item);
        }
        return '';
      };
      this.setSetting = (key, value) => {
        localStorage.setItem(key, JSON.stringify(value));
      };
      this.removeSetting = key => {
        localStorage.removeItem(key);
      };
      this.getAll = () => {
        return localStorage;
      };
    }
    static {
      this.ɵfac = function StoredSettingService_Factory(t) {
        return new (t || StoredSettingService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: StoredSettingService,
        factory: StoredSettingService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return StoredSettingService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let EasyXDMService = /*#__PURE__*/(() => {
  class EasyXDMService {
    MsgPromise(serverURL, msg, namespace) {
      if (namespace === '') {
        namespace = 'Defaultns';
      }
      this.dataPromise = new Promise((resolve, reject) => {
        this.socket = new easyXDM.Socket({
          onMessage: (message, origin) => {
            resolve(message);
          },
          remote: serverURL
        });
        this.socket.postMessage(msg);
      });
      return this.dataPromise;
    }
    RpcPromise(serverURL, procName, params, namespace) {
      if (namespace === '') {
        namespace = 'Defaultns';
      }
      this.remoteDataPromise = new Promise((resolve, reject) => {
        this.remote = new easyXDM.Rpc({
          remote: serverURL
        }, {
          remote: {
            postMessage: {}
          }
        });
        switch (procName) {
          case 'postMessage':
            this.remote.postMessage(params, result => {
              resolve(result);
            });
            break;
          default:
        }
      });
      return this.remoteDataPromise;
    }
    IFramePromise(serverURL, frameContainer, frameWidth, frameHeight, namespace) {
      if (namespace === '') {
        namespace = 'Defaultns';
      }
      this.remoteFramePromise = new Promise((resolve, reject) => {
        this.frameSocket = new easyXDM.Socket({
          container: document.getElementById(frameContainer),
          onMessage: (message, origin) => {
            resolve(message);
          },
          onReady: function () {
            try {
              if (frameWidth !== '') {
                document.getElementById(frameContainer).getElementsByTagName('iframe')[0].style.width = frameWidth;
              }
              if (frameHeight !== '') {
                document.getElementById(frameContainer).getElementsByTagName('iframe')[0].style.height = frameHeight;
              }
            } catch (err) {
              console.log(err);
            }
          },
          remote: serverURL
        });
      });
      return this.remoteFramePromise;
    }
    MsgObservable(serverURL, msg, namespace) {
      if (namespace === '') {
        namespace = 'Defaultns';
      }
      this.data = new Observable(observer => {
        this.socket = new easyXDM.Socket({
          onMessage: (message, origin) => {
            observer.next(message);
            observer.complete();
          },
          remote: serverURL
        });
        this.socket.postMessage(msg);
      });
      return this.data;
    }
    RpcObservable(serverURL, procName, params, namespace) {
      if (namespace === '') {
        namespace = 'Defaultns';
      }
      this.remoteData = new Observable(observer => {
        this.remote = new easyXDM.Rpc({
          remote: serverURL
        }, {
          remote: {
            postMessage: {}
          }
        });
        switch (procName) {
          case 'postMessage':
            this.remote.postMessage(params, result => {
              observer.next(result);
              observer.complete();
            });
            break;
          default:
        }
      });
      return this.remoteData;
    }
    IFrameObservable(serverURL, frameContainer, frameWidth, frameHeight, namespace) {
      if (namespace === '') {
        namespace = 'Defaultns';
      }
      this.remoteFrame = new Observable(observer => {
        this.frameSocket = new easyXDM.Socket({
          container: document.getElementById(frameContainer),
          onMessage: (message, origin) => {
            observer.next(message);
            observer.complete();
          },
          onReady: function () {
            try {
              if (frameWidth !== '') {
                document.getElementById(frameContainer).getElementsByTagName('iframe')[0].style.width = frameWidth;
              }
              if (frameHeight !== '') {
                document.getElementById(frameContainer).getElementsByTagName('iframe')[0].style.height = frameHeight;
              }
            } catch (err) {
              console.log(err);
            }
          },
          remote: serverURL
        });
      });
      return this.remoteFrame;
    }
    static {
      this.ɵfac = function EasyXDMService_Factory(t) {
        return new (t || EasyXDMService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: EasyXDMService,
        factory: EasyXDMService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return EasyXDMService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class Parameter {
  constructor() {
    this.key = '';
  }
}

/*
 * Public API Surface of angular-highfieldwebapi
 */

/**
 * Generated bundle index. Do not edit.
 */

export { APIResponse, APIService, AngularHighfieldwebapiModule, EasyXDMService, GenericAPIResponse, Parameter, StoredSettingService };
